import React, { useRef, useState } from 'react';
import styles from './Slider.module.css';

/**
 * Slider — a shadcn-style range input, reimplemented in Athena's idiom
 * (TSX + CSS Module + 2.0 tokens). No Radix dependency: the track + range fill
 * are styled spans and every thumb is a focusable `role="slider"` button driven
 * by Pointer Events off the track's getBoundingClientRect() — the same
 * single-coordinate-system technique as the admin-config SingleSlider /
 * ThresholdRange.
 *
 * Supports 1 thumb (single), 2 (range), or N (multiple) via the `value` array;
 * controlled (`value` + `onValueChange`) or uncontrolled (`defaultValue`);
 * horizontal or vertical. Keyboard: ←/↓ −step, →/↑ +step, PageUp/Down ±step*10,
 * Home/End to the thumb's clamped bounds. Thumbs neighbour-clamp so they never
 * cross. Tokens cascade light/dark, so no [data-theme] overrides.
 */

export type SliderOrientation = 'horizontal' | 'vertical';

export interface SliderProps {
  /** Controlled values (1 = single, 2 = range, N = multiple thumbs). */
  value?: number[];
  /** Uncontrolled initial values. Defaults to `[min]`. */
  defaultValue?: number[];
  min?: number;
  max?: number;
  step?: number;
  orientation?: SliderOrientation;
  disabled?: boolean;
  /** Fires on every change (drag / keyboard). */
  onValueChange?: (next: number[]) => void;
  /** Fires once on pointer-up / key-up with the settled values. */
  onValueCommit?: (next: number[]) => void;
  /** Label applied to a single thumb. */
  'aria-label'?: string;
  /** Per-thumb labels (multi-thumb). */
  ariaLabels?: string[];
  /** Range-fill + thumb-accent colour (any CSS colour / token). Falls back to
   *  --primary. Use it to drive a domain colour scheme — e.g. pass the
   *  attack-feasibility colour (getFeasibilityColor / getValueColor) so the
   *  fill reflects the tier, the way the old ThresholdRange / SingleSlider did. */
  color?: string;
  className?: string;
}

const Slider = ({
  value,
  defaultValue,
  min = 0,
  max = 100,
  step = 1,
  orientation = 'horizontal',
  disabled = false,
  onValueChange,
  onValueCommit,
  ariaLabels,
  color,
  className = '',
  ...props
}: SliderProps) => {
  const isControlled = value !== undefined;
  const [internal, setInternal] = useState<number[]>(defaultValue ?? [min]);
  const values = isControlled ? (value as number[]) : internal;

  const trackRef = useRef<HTMLSpanElement>(null);
  // Mirror of the latest values so pointer/key handlers can commit without
  // chasing stale closures.
  const valuesRef = useRef<number[]>(values);
  valuesRef.current = values;

  const span = max - min || 1;
  const horizontal = orientation === 'horizontal';

  const valueToPercent = (v: number) => ((v - min) / span) * 100;
  const clamp = (v: number) => Math.min(max, Math.max(min, v));
  const roundToStep = (raw: number) => {
    const snapped = Math.round((raw - min) / step) * step + min;
    // Avoid floating-point drift for fractional steps.
    return Number(snapped.toFixed(6));
  };

  const clientToValue = (clientX: number, clientY: number) => {
    const track = trackRef.current;
    if (!track) return min;
    const rect = track.getBoundingClientRect();
    const ratio = horizontal
      ? (clientX - rect.left) / rect.width
      : (rect.bottom - clientY) / rect.height; // bottom = min, top = max
    return clamp(roundToStep(min + Math.min(1, Math.max(0, ratio)) * span));
  };

  // Update thumb `i`, neighbour-clamped so thumbs never cross. Returns the
  // next array so drag/key handlers can commit it.
  const setThumb = (i: number, next: number) => {
    const current = valuesRef.current;
    const lo = i > 0 ? current[i - 1] : min;
    const hi = i < current.length - 1 ? current[i + 1] : max;
    const clamped = Math.min(hi, Math.max(lo, next));
    if (clamped === current[i]) return current;
    const out = current.slice();
    out[i] = clamped;
    valuesRef.current = out;
    if (!isControlled) setInternal(out);
    onValueChange?.(out);
    return out;
  };

  const beginDrag = (
    i: number,
    e: React.PointerEvent,
    captureEl: HTMLElement,
  ) => {
    if (disabled) return;
    e.preventDefault();
    try {
      captureEl.setPointerCapture(e.pointerId);
    } catch (_err) {
      /* stale pointerId — safe to ignore */
    }

    setThumb(i, clientToValue(e.clientX, e.clientY));

    const onMove = (ev: PointerEvent) =>
      setThumb(i, clientToValue(ev.clientX, ev.clientY));
    const onUp = (ev: PointerEvent) => {
      try {
        captureEl.releasePointerCapture(ev.pointerId);
      } catch (_err) {
        /* ignore */
      }
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      onValueCommit?.(valuesRef.current);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  const onThumbPointerDown = (i: number) => (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).focus();
    beginDrag(i, e, e.currentTarget as HTMLElement);
  };

  // Pointer-down on the track (not a thumb): grab the nearest thumb and drag it.
  const onTrackPointerDown = (e: React.PointerEvent) => {
    if (disabled) return;
    if ((e.target as HTMLElement).dataset.slot === 'slider-thumb') return;
    const target = clientToValue(e.clientX, e.clientY);
    let nearest = 0;
    let best = Infinity;
    values.forEach((v, i) => {
      const d = Math.abs(v - target);
      if (d < best) {
        best = d;
        nearest = i;
      }
    });
    beginDrag(nearest, e, trackRef.current as HTMLElement);
  };

  const onKeyDown = (i: number) => (e: React.KeyboardEvent) => {
    if (disabled) return;
    const v = valuesRef.current[i];
    const lo = i > 0 ? valuesRef.current[i - 1] : min;
    const hi = i < valuesRef.current.length - 1 ? valuesRef.current[i + 1] : max;
    let next = v;
    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        next = v - step;
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        next = v + step;
        break;
      case 'PageDown':
        next = v - step * 10;
        break;
      case 'PageUp':
        next = v + step * 10;
        break;
      case 'Home':
        next = lo;
        break;
      case 'End':
        next = hi;
        break;
      default:
        return;
    }
    e.preventDefault();
    setThumb(i, clamp(next));
  };

  const onKeyUp = (e: React.KeyboardEvent) => {
    const keys = [
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'PageUp',
      'PageDown',
      'Home',
      'End',
    ];
    if (keys.includes(e.key)) onValueCommit?.(valuesRef.current);
  };

  const lo = valueToPercent(Math.min(...values));
  const hi = valueToPercent(Math.max(...values));
  const fillStart = values.length === 1 ? 0 : lo;
  const fillEnd = values.length === 1 ? valueToPercent(values[0]) : hi;

  const rangeStyle = horizontal
    ? { left: `${fillStart}%`, right: `${100 - fillEnd}%` }
    : { bottom: `${fillStart}%`, top: `${100 - fillEnd}%` };

  return (
    <span
      ref={trackRef}
      data-orientation={orientation}
      data-disabled={disabled || undefined}
      className={`${styles.root} ${horizontal ? '' : styles.vertical} ${className}`.trim()}
      style={color ? ({ ['--slider-accent']: color } as React.CSSProperties) : undefined}
      onPointerDown={disabled ? undefined : onTrackPointerDown}
      {...props}
    >
      <span className={styles.track}>
        <span className={styles.range} style={rangeStyle} aria-hidden="true" />
      </span>

      {values.map((v, i) => (
        <button
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          type="button"
          role="slider"
          data-slot="slider-thumb"
          className={styles.thumb}
          style={horizontal ? { left: `${valueToPercent(v)}%` } : { bottom: `${valueToPercent(v)}%` }}
          disabled={disabled}
          aria-orientation={orientation}
          aria-valuemin={i > 0 ? values[i - 1] : min}
          aria-valuemax={i < values.length - 1 ? values[i + 1] : max}
          aria-valuenow={v}
          aria-label={ariaLabels?.[i] ?? props['aria-label']}
          onPointerDown={onThumbPointerDown(i)}
          onKeyDown={onKeyDown(i)}
          onKeyUp={onKeyUp}
        />
      ))}
    </span>
  );
};

export default Slider;
export { Slider };
